import { Fragment, FunctionComponent } from 'react'
import useStyles from './styles'
import {
  Box,
  Button,
  Text,
  getTheme,
  useMediaQuery,
} from '@conexasaude/conexa-hero'
import { DataConfirmationSectionType } from './types'
import { RenderIf } from '@/components/RenderIf'
import { Icon } from '@conexasaude/iconography/icons'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { capitalize } from '@/utils/fnUtils'
import { RELATIONSHIP_OPTIONS } from '@/modules/Attendance/constants'
import { formattedCellphone } from '../ConfirmContactWrapper/utils'

const DataConfirmationSection: FunctionComponent<
  DataConfirmationSectionType
> = ({ handleClick, hideHeader, sexes }) => {
  const styles = useStyles()
  const theme = getTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.grid.breakpoint.sm} )`)
  const { user, clinic } = useAuthContext()

  const fullAddress = () => {
    const requiredAddressFields = {
      cep: user?.address?.cep,
      streetName: user?.address?.streetName,
      number: user?.address?.number,
      neighborhood: user?.address?.neighborhood,
      city: user?.address?.city,
      state: user?.address?.state,
    }
    if (Object.values(requiredAddressFields).filter(value => !value).length)
      return 'Dados incompletos'
    return `${user?.address?.streetName},
     ${user?.address?.number}, ${user?.address?.neighborhood},
     ${user?.address?.city} - ${user?.address?.state.toUpperCase()}`
  }
  const tratativeNumberCellphone = user?.cellphone?.replace(')', ') ')

  const sexLabel = () => {
    if (!user?.sex) return '-'
    const selectedSex = sexes?.find(sex => sex?.value === user?.sex)
    return selectedSex?.label
  }
  const relationshipLabel = () => {
    if (!user?.contactsEmergency?.relationship) return '-'
    const selectedRel = RELATIONSHIP_OPTIONS?.find(
      rel => rel?.value === user?.contactsEmergency?.relationship,
    )
    return selectedRel?.label
  }

  const pregnancyTimeLabel = () => {
    let displayedSentenceDays = ''
    let displayedSentenceWeeks = ''
    if (
      !user?.pregnantInformation?.daysPregnancy &&
      !user?.pregnantInformation?.weeksPregnancy
    )
      return '-'
    if (
      !!user?.pregnantInformation?.daysPregnancy &&
      user?.pregnantInformation?.daysPregnancy === '1'
    ) {
      displayedSentenceDays = `${user?.pregnantInformation?.daysPregnancy} dia`
    } else {
      displayedSentenceDays = `${user?.pregnantInformation?.daysPregnancy} dias`
    }
    if (
      !!user?.pregnantInformation?.weeksPregnancy &&
      user?.pregnantInformation?.weeksPregnancy === '1'
    ) {
      displayedSentenceWeeks = `${user?.pregnantInformation?.weeksPregnancy} semana`
    } else {
      displayedSentenceWeeks = `${user?.pregnantInformation?.weeksPregnancy} semanas`
    }
    return `${displayedSentenceWeeks} e ${displayedSentenceDays}`
  }

  return (
    <Fragment>
      <RenderIf condition={!hideHeader}>
        <div css={styles.heading}>
          <Text variant="headingLarge">Confirme seus dados</Text>
          <Text variant="subheading">
            Mantenha seus dados atualizados para possíveis emergências.
          </Text>
        </div>
      </RenderIf>

      <Box>
        <RenderIf condition={isMobile}>
          <div css={styles.boxHeader}>
            <Icon
              onClick={handleClick}
              name="edit"
              color={theme.colors.brand.primary[300]}
            />
          </div>
        </RenderIf>
        <div css={styles.containerWrapper}>
          <div css={styles.sideContent}>
            <Text
              color={theme.colors.brand.primary[500]}
              variant="headingSmall"
            >
              Dados pessoais
            </Text>
            <div>
              <Text renderAs="span" variant="paragraphSmall">
                Seu e-mail
              </Text>
              <Text>{user?.email || '-'}</Text>
            </div>
            <div>
              <Text renderAs="span" variant="paragraphSmall">
                Seu celular
              </Text>
              <Text>{formattedCellphone(tratativeNumberCellphone) || '-'}</Text>
            </div>
            <div>
              <Text renderAs="span" variant="paragraphSmall">
                Data de nascimento
              </Text>
              <Text>{user?.birthdate || '-'}</Text>
            </div>
            <div>
              <Text renderAs="span" variant="paragraphSmall">
                Sexo biológico
              </Text>
              <Text>{sexLabel()}</Text>
            </div>
            <div>
              <Text renderAs="span" variant="paragraphSmall">
                Seu endereço
              </Text>
              <Text>{fullAddress()}</Text>
            </div>
          </div>
          <RenderIf condition={isMobile}>
            <hr />
          </RenderIf>
          <div css={styles.sideContent}>
            <Text
              color={theme.colors.brand.primary[500]}
              variant="headingSmall"
            >
              Dados do contato de emergência
            </Text>
            <div>
              <Text renderAs="span" variant="paragraphSmall">
                Nome completo
              </Text>
              <Text css={styles.fullName}>
                {capitalize(
                  user?.contactsEmergency?.emergencyContactFullName?.toLocaleLowerCase(),
                ) || '-'}
              </Text>
            </div>
            <div>
              <Text renderAs="span" variant="paragraphSmall">
                Parentesco
              </Text>
              <Text>{relationshipLabel()}</Text>
            </div>
            <div>
              <Text renderAs="span" variant="paragraphSmall">
                Telefone do contato de emergência
              </Text>
              <Text>
                {formattedCellphone(
                  user?.contactsEmergency?.emergencyContactCellphone,
                ) || '-'}
              </Text>
            </div>
          </div>
        </div>
        <RenderIf condition={clinic?.flag?.HabilitarFluxoDadosGestacionais}>
          <div css={styles.containerWrapper}>
            <div css={styles.sideContent}>
              <div css={styles.pregnancyTitle}>
                <Text
                  color={theme.colors.brand.primary[500]}
                  variant="headingSmall"
                >
                  Dados da pessoa gestante
                </Text>
              </div>
              <div>
                <Text renderAs="span" variant="paragraphSmall">
                  Tempo de gestação
                </Text>
                <Text css={styles.fullName}>{pregnancyTimeLabel()}</Text>
              </div>
              <div>
                <Text renderAs="span" variant="paragraphSmall">
                  Data da última menstruação
                </Text>
                <Text>
                  {user?.pregnantInformation?.lastMenstrualPeriodDate || '-'}
                </Text>
              </div>
            </div>
          </div>
        </RenderIf>
        <RenderIf condition={!isMobile}>
          <div css={styles.footer}>
            <Button onClick={handleClick} variant="secondary">
              Editar dados
            </Button>
          </div>
        </RenderIf>
      </Box>
    </Fragment>
  )
}

export default DataConfirmationSection
